import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Pause, Play, SkipBack, Upload } from 'lucide-react';
import api from '../api/client';
import { SavedServer, SeasonConfig } from '../types';
import './LogReplayTool.css';

interface LogEntry {
  timestamp: number;
  level: string;
  message: string;
}

interface TelemetryEntry {
  position?: number;
  driverName?: string;
  carName?: string;
  carNumber?: string;
  bestLapMs?: number;
  lastLapMs?: number;
  lapsCompleted?: number;
  pitStops?: number;
  status?: string;
  disconnected?: boolean;
  hasCollision?: boolean;
  collisionDetails?: { withWhat: string; hasDamage: boolean };
}

interface SessionInfo {
  code: string;
  label: string;
  hasStarted: boolean;
  hasEnded: boolean;
}

interface ReplayState {
  code: string | null;
  label: string;
  progressPercent: number;
  sessionEnded?: boolean;
  allSessions?: SessionInfo[];
  liveData: TelemetryEntry[];
}

interface Snapshot {
  timestamp: number;
  state: ReplayState;
}

const SPEEDS = [1, 2, 5, 10, 50] as const;
type Speed = typeof SPEEDS[number];

const formatMs = (ms?: number) => {
  if (ms == null || ms <= 0) return '—';
  const totalSeconds = ms / 1000;
  const mins = Math.floor(totalSeconds / 60);
  const secs = (totalSeconds % 60).toFixed(3).padStart(6, '0');
  return `${mins}:${secs}`;
};

const formatTimestamp = (ts: number) => new Date(ts).toLocaleTimeString();

interface LogReplayToolProps {
  servers: SavedServer[];
}

const LogReplayTool: React.FC<LogReplayToolProps> = ({ servers }) => {
  const [logEntries, setLogEntries] = useState<LogEntry[]>([]);
  const [fileName, setFileName] = useState('');
  const [seasonConfig, setSeasonConfig] = useState<SeasonConfig | null>(null);
  const [selectedServerId, setSelectedServerId] = useState('');
  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);
  const [cursor, setCursor] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState<Speed>(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const cursorRef = useRef(cursor);
  cursorRef.current = cursor;

  // Advance cursor at real-time speed × multiplier
  // Each snapshot has a real timestamp; we advance cursor based on how much wall-clock time passes
  const lastWallRef = useRef<number>(0);

  const stopPlayback = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setPlaying(false);
  }, []);

  const startPlayback = useCallback(() => {
    if (snapshots.length < 2) return;
    lastWallRef.current = Date.now();
    setPlaying(true);
  }, [snapshots]);

  // Accumulated sim-time budget (ms) not yet consumed by cursor advancement
  const simDebtRef = useRef<number>(0);

  useEffect(() => {
    if (!playing || snapshots.length < 2) return;
    simDebtRef.current = 0;

    const TICK_MS = 100;
    timerRef.current = setInterval(() => {
      const now = Date.now();
      const wallElapsed = now - lastWallRef.current;
      lastWallRef.current = now;

      simDebtRef.current += wallElapsed * speed;

      setCursor((prev) => {
        let next = prev;
        // Advance through as many snapshots as the accumulated sim time allows
        while (next < snapshots.length - 1) {
          const gap = snapshots[next + 1].timestamp - snapshots[next].timestamp;
          if (gap <= 0 || simDebtRef.current >= gap) {
            simDebtRef.current -= Math.max(0, gap);
            next++;
          } else {
            break;
          }
        }
        if (next >= snapshots.length - 1) {
          stopPlayback();
          return snapshots.length - 1;
        }
        return next;
      });
    }, TICK_MS);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [playing, speed, snapshots, stopPlayback]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setSnapshots([]);
    setCursor(0);
    stopPlayback();

    try {
      const text = await file.text();
      const parsed = JSON.parse(text);

      // Support both raw array of entries and the { serverId, entries } log file format
      const entries: LogEntry[] = Array.isArray(parsed)
        ? parsed
        : Array.isArray(parsed.entries)
        ? parsed.entries
        : [];

      if (entries.length === 0) {
        setError('No log entries found in file. Expected { entries: [...] } or a raw array.');
        return;
      }

      setLogEntries(entries);
      setFileName(file.name);
    } catch {
      setError('Failed to parse log file — must be valid JSON.');
    }

    // Reset input so the same file can be re-uploaded
    e.target.value = '';
  };

  // When server selection changes, pick its seasonConfig
  useEffect(() => {
    if (!selectedServerId) {
      setSeasonConfig(null);
      return;
    }
    const server = servers.find((s) => s.id === selectedServerId);
    setSeasonConfig(server?.seasonConfig ?? null);
  }, [selectedServerId, servers]);

  const handleGenerateSnapshots = async () => {
    if (!logEntries.length) {
      setError('Upload a log file first.');
      return;
    }
    if (!seasonConfig) {
      setError('Select a server to use its session config (track, session types, durations).');
      return;
    }

    setLoading(true);
    setError('');
    setSnapshots([]);
    setCursor(0);
    stopPlayback();

    try {
      const res = await api.debugReplaySnapshots(logEntries, seasonConfig);
      const snaps: Snapshot[] = res.data.snapshots;
      if (!snaps || snaps.length === 0) {
        setError('No replay snapshots generated — log may contain no telemetry events.');
        return;
      }
      setSnapshots(snaps);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to generate replay snapshots.');
    } finally {
      setLoading(false);
    }
  };

  const currentState: ReplayState | null = snapshots[cursor]?.state ?? null;
  const isRace = currentState?.code === 'R';
  const progress = snapshots.length > 1 ? (cursor / (snapshots.length - 1)) * 100 : 0;

  const elapsedSimMs =
    snapshots.length > 1
      ? snapshots[cursor].timestamp - snapshots[0].timestamp
      : 0;

  const totalSimMs =
    snapshots.length > 1
      ? snapshots[snapshots.length - 1].timestamp - snapshots[0].timestamp
      : 0;

  const formatDuration = (ms: number) => {
    const s = Math.floor(ms / 1000);
    const m = Math.floor(s / 60);
    const h = Math.floor(m / 60);
    if (h > 0) return `${h}h ${m % 60}m`;
    if (m > 0) return `${m}m ${s % 60}s`;
    return `${s}s`;
  };

  return (
    <div className="log-replay-tool">
      <div className="replay-header">
        <h2>Log Replay</h2>
        <p className="replay-subtitle">
          Upload a saved server log file to replay telemetry as if the session were live.
        </p>
      </div>

      <div className="replay-setup">
        <div className="replay-setup-row">
          <label className="replay-upload-label">
            <Upload size={15} />
            {fileName || 'Upload log file (.json)'}
            <input
              type="file"
              accept=".json,.log"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
          </label>
          {logEntries.length > 0 && (
            <span className="replay-entry-count">{logEntries.length.toLocaleString()} log entries</span>
          )}
        </div>

        <div className="replay-setup-row">
          <label className="replay-field-label">Server config</label>
          <select
            className="replay-select"
            value={selectedServerId}
            onChange={(e) => setSelectedServerId(e.target.value)}
          >
            <option value="">— pick a server for session config —</option>
            {servers.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.seasonConfig?.event?.track ?? '?'})
              </option>
            ))}
          </select>
        </div>

        <button
          className="replay-generate-btn"
          onClick={handleGenerateSnapshots}
          disabled={loading || !logEntries.length || !seasonConfig}
        >
          {loading ? 'Processing…' : 'Generate replay'}
        </button>

        {error && <p className="replay-error">{error}</p>}
      </div>

      {snapshots.length > 0 && (
        <div className="replay-player">
          {/* Transport bar */}
          <div className="replay-transport">
            <div className="transport-left">
              <button
                className="transport-btn"
                title="Restart"
                onClick={() => { stopPlayback(); setCursor(0); }}
              >
                <SkipBack size={16} />
              </button>
              <button
                className="transport-btn transport-play"
                onClick={() => (playing ? stopPlayback() : startPlayback())}
                disabled={cursor >= snapshots.length - 1}
              >
                {playing ? <Pause size={18} /> : <Play size={18} />}
              </button>
              <div className="speed-buttons">
                {SPEEDS.map((s) => (
                  <button
                    key={s}
                    className={`speed-btn ${speed === s ? 'active' : ''}`}
                    onClick={() => setSpeed(s)}
                  >
                    ×{s}
                  </button>
                ))}
              </div>
            </div>
            <div className="transport-right">
              <span className="transport-time">
                {formatDuration(elapsedSimMs)} / {formatDuration(totalSimMs)}
              </span>
              <span className="transport-walltime">
                {snapshots[cursor] ? formatTimestamp(snapshots[cursor].timestamp) : '—'}
              </span>
            </div>
          </div>

          {/* Scrubber */}
          <input
            type="range"
            className="replay-scrubber"
            min={0}
            max={snapshots.length - 1}
            value={cursor}
            onChange={(e) => { stopPlayback(); setCursor(Number(e.target.value)); }}
          />

          {/* Session state */}
          {currentState && (
            <div className="replay-session-bar">
              <div className="session-pills">
                {currentState.allSessions?.map((s) => (
                  <span
                    key={s.code}
                    className={`session-pill-badge ${s.code === currentState.code ? 'active' : ''} ${!s.hasStarted ? 'upcoming' : s.hasEnded ? 'ended' : ''}`}
                  >
                    {s.label}
                  </span>
                ))}
              </div>
              <span className="replay-session-label">
                {currentState.label}
                {currentState.sessionEnded && <span className="ended-tag"> · ENDED</span>}
              </span>
              <div className="replay-progress-bar">
                <div className="replay-progress-fill" style={{ width: `${currentState.progressPercent}%` }} />
              </div>
            </div>
          )}

          {/* Telemetry table */}
          {currentState && (
            <div className="replay-table-wrap">
              {currentState.liveData.length === 0 ? (
                <p className="replay-empty">No drivers on track yet.</p>
              ) : (
                <table className="replay-table">
                  <thead>
                    <tr>
                      <th className="col-pos">Pos</th>
                      <th className="col-driver">Driver</th>
                      <th className="col-car-num">#</th>
                      <th className="col-car">Car</th>
                      <th className="col-best">Best Lap</th>
                      <th className="col-last">Last Lap</th>
                      {isRace && <><th className="col-laps">Laps</th><th className="col-pits">Pits</th></>}
                      <th className="col-status">Status</th>
                      <th className="col-ev">Events</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentState.liveData.map((entry, idx) => (
                      <tr
                        key={`${entry.driverName ?? 'driver'}-${idx}`}
                        className={entry.disconnected ? 'row-left' : ''}
                      >
                        <td className="col-pos">{entry.position ?? idx + 1}</td>
                        <td className="col-driver">
                          {entry.driverName || '—'}
                          {entry.disconnected && <span className="left-tag"> (left)</span>}
                        </td>
                        <td className="col-car-num">{entry.carNumber || '—'}</td>
                        <td className="col-car">{entry.carName || '—'}</td>
                        <td className="col-best">{formatMs(entry.bestLapMs)}</td>
                        <td className="col-last">{formatMs(entry.lastLapMs)}</td>
                        {isRace && (
                          <>
                            <td className="col-laps">{entry.lapsCompleted ?? '—'}</td>
                            <td className="col-pits">{entry.pitStops ?? 0}</td>
                          </>
                        )}
                        <td className="col-status">{entry.status || 'On Track'}</td>
                        <td className="col-ev">
                          {entry.hasCollision && (
                            <span
                              className="collision-badge"
                              title={entry.collisionDetails
                                ? `Collision with ${entry.collisionDetails.withWhat === 'car' && entry.collisionDetails.otherDriver ? entry.collisionDetails.otherDriver : entry.collisionDetails.withWhat}${entry.collisionDetails.hasDamage ? ' · damaged' : ''}`
                                : 'Collision'}
                            >
                              <AlertTriangle size={13} />
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          <div className="replay-footer">
            <span>{snapshots.length.toLocaleString()} snapshots · snapshot {cursor + 1}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default LogReplayTool;
